import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import Swal from "sweetalert2";
import axios from "axios";
import {
  FaUser,
  FaMapMarkerAlt,
  FaGraduationCap,
  FaUpload,
  FaSyncAlt,
  FaPaperPlane,
  FaBriefcase,
  FaListOl,
  FaBookReader,
} from "react-icons/fa";
import "./AdmissionForm.css";

import logo from "../../assets/sk-tt.jpg";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdmissionForm = () => {
  // Helper to get current dynamic session years (e.g., 2026 -> start: "26", end: "27")
  const currentYear = new Date().getFullYear();
  const startYearDefault = String(currentYear).slice(-2);
  const endYearDefault = String(currentYear + 1).slice(-2);

  // --- STATE ---
  const [formData, setFormData] = useState({
    // Session (Auto-filled)
    sessionStart: startYearDefault,
    sessionEnd: endYearDefault,

    // Personal
    studentName: "",
    fatherName: "",
    motherName: "",
    dob: "",
    gender: "",
    bloodGroup: "",
    aadharNumber: "",
    caste: "",
    religion: "",
    category: "",
    categoryCertificate: "",

    // Special Reservation
    isDisabled: false,
    disabledCert: "",
    isExService: false,
    exServiceCert: "",
    isWidow: false,
    widowCert: "",

    // Contact
    permanentAddress: "",
    currentAddress: "",
    sameAddress: false,
    mobile: "",
    email: "",

    // Service Details
    designation: "",
    department: "",
    postingAddress: "",

    // Counseling Details
    faculty: "",
    admissionSerial: "",
    counselingCategory: "",
    rollNo: "",
    totalEntranceMarks: "",
    meritRank: "",
    district: "",

    // Education
    eduHighSchool: {
      total: "",
      obtained: "",
      percent: "",
      div: "",
      subj: "",
      board: "",
      cert: "",
    },
    eduHigherSec: {
      total: "",
      obtained: "",
      percent: "",
      div: "",
      subj: "",
      board: "",
      cert: "",
    },
    eduGraduation: {
      total: "",
      obtained: "",
      percent: "",
      div: "",
      subj: "",
      board: "",
      cert: "",
    },
    eduPostGrad: {
      total: "",
      obtained: "",
      percent: "",
      div: "",
      subj: "",
      board: "",
      cert: "",
    },
    eduOther: {
      total: "",
      obtained: "",
      percent: "",
      div: "",
      subj: "",
      board: "",
      cert: "",
    },

    // Training Subjects
    trainingSub1: "",
    trainingSub2: "",

    declaration: false,
  });

  const [files, setFiles] = useState({ photo: null });
  const [previews, setPreviews] = useState({ photo: null });
  const [captcha, setCaptcha] = useState("");
  const [userCaptcha, setUserCaptcha] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOtherReligion, setIsOtherReligion] = useState(false);

  const fileRefs = { photo: useRef(null) };

  // --- CAPTCHA & INIT ---
  const generateCaptcha = () => {
    const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let code = "";
    for (let i = 0; i < 6; i++) {
      code += chars[Math.floor(Math.random() * chars.length)];
    }
    setCaptcha(code);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  // Cleanup preview URLs
  useEffect(() => {
    return () => {
      Object.values(previews).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [previews]);

  // --- HANDLERS ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // 1. Session Logic (Max 2 digits, numbers only)
    if (name === "sessionStart" || name === "sessionEnd") {
      const re = /^[0-9\b]+$/;
      if (value === "" || (re.test(value) && value.length <= 2)) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }

    // 2. Text Only Validation (Names, Caste, Religion)
    if (
      ["studentName", "fatherName", "motherName", "caste", "religion"].includes(
        name,
      )
    ) {
      // Regex: Only alphabets and spaces
      const re = /^[A-Za-z\s]+$/;
      if (value === "" || re.test(value)) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }

    // 3. Number Only Validation (Aadhar, Mobile)
    if (["aadharNumber", "mobile"].includes(name)) {
      // Regex: Only digits
      const re = /^[0-9]+$/;
      // Limit Aadhar to 12, Mobile to 10
      const limit = name === "aadharNumber" ? 12 : 10;

      if (value === "" || (re.test(value) && value.length <= limit)) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }

    // 4. Same Address Logic
    if (name === "sameAddress") {
      setFormData((prev) => ({
        ...prev,
        sameAddress: checked,
        currentAddress: checked ? prev.permanentAddress : "",
      }));
      return;
    }

    // 5. Special Reservation Checkbox Logic
    if (["isDisabled", "isExService", "isWidow"].includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleReligionDropdownChange = (e) => {
    const val = e.target.value;
    if (val === "Other") {
      setIsOtherReligion(true);
      setFormData((prev) => ({ ...prev, religion: "" }));
    } else {
      setIsOtherReligion(false);
      setFormData((prev) => ({ ...prev, religion: val }));
    }
  };

  const handleAddressBlur = () => {
    if (formData.sameAddress) {
      setFormData((prev) => ({
        ...prev,
        currentAddress: prev.permanentAddress,
      }));
    }
  };

  const handleEducationChange = (level, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [level]: { ...prev[level], [field]: value },
    }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const sizeKB = file.size / 1024;
    if (fieldName === "photo") {
      if (sizeKB < 5 || sizeKB > 200) {
        Swal.fire(
          "Invalid Size",
          "Photo must be between 5KB and 200KB.",
          "warning",
        );
        e.target.value = null;
        return;
      }
    }

    setFiles((prev) => ({ ...prev, [fieldName]: file }));
    if (previews[fieldName]) URL.revokeObjectURL(previews[fieldName]);
    setPreviews((prev) => ({
      ...prev,
      [fieldName]: URL.createObjectURL(file),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Regex Validations
    if (formData.mobile.length !== 10)
      return Swal.fire(
        "Error",
        "Please enter a valid 10-digit mobile number.",
        "error",
      );
    if (formData.aadharNumber.length !== 12)
      return Swal.fire(
        "Error",
        "Please enter a valid 12-digit Aadhar number.",
        "error",
      );

    // Email Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email))
      return Swal.fire("Error", "Please enter a valid email address.", "error");

    if (userCaptcha.toUpperCase() !== captcha)
      return Swal.fire("Error", "Incorrect Captcha Code.", "error");
    if (!files.photo)
      return Swal.fire("Error", "Passport photo is required.", "error");

    // Conditional Validations
    if (formData.category !== "General" && !formData.categoryCertificate)
      return Swal.fire(
        "Error",
        "Category Certificate Number is required.",
        "warning",
      );
    if (formData.isDisabled && !formData.disabledCert)
      return Swal.fire(
        "Error",
        "Disability Certificate Number is required.",
        "warning",
      );
    if (formData.isExService && !formData.exServiceCert)
      return Swal.fire(
        "Error",
        "Ex-Serviceman Certificate Number is required.",
        "warning",
      );
    if (formData.isWidow && !formData.widowCert)
      return Swal.fire(
        "Error",
        "Widow/Deserted Certificate Number is required.",
        "warning",
      );

    if (!formData.declaration)
      return Swal.fire("Error", "Please accept the declaration.", "warning");

    setIsLoading(true);

    const data = new FormData();
    const flatData = { ...formData };
    [
      "eduHighSchool",
      "eduHigherSec",
      "eduGraduation",
      "eduPostGrad",
      "eduOther",
    ].forEach((key) => {
      flatData[key] = JSON.stringify(formData[key]);
    });

    Object.keys(flatData).forEach((key) => {
      data.append(key, flatData[key]);
    });
    Object.keys(files).forEach((key) => {
      if (files[key]) data.append(key, files[key]);
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admissions/submit`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.status === 201 || response.status === 200) {
        Swal.fire({
          title: "Application Submitted!",
          text: "Form submitted successfully.",
          icon: "success",
        }).then(() => window.location.reload());
      }
    } catch (error) {
      Swal.fire(
        "Submission Failed",
        error.response?.data?.message || "An error occurred.",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          B.Ed Admission Form | Sucheta Kriplani Teacher Training College
          (SKTTC), Jodhpur
        </title>

        <meta
          name="description"
          content="Apply online for the B.Ed program at Sucheta Kriplani Teacher Training College (SKTTC), Manaklao, Jodhpur. NCTE-approved and affiliated with JNVU. Admissions are based on Rajasthan PTET counseling."
        />

        <meta
          name="keywords"
          content="SKTTC Admission Form, B.Ed Admission Jodhpur, PTET Counseling College, Sucheta Kriplani Teacher Training College Admission, JNVU Affiliated B.Ed College"
        />

        <link
          rel="canonical"
          href="https://suchetabedcollege.org/admission-form"
        />
      </Helmet>

      <div className="teacher-training-form-sktt-wrapper">
        <div className="teacher-training-form-sktt-container">
          {/* --- HEADER --- */}
          <div className="teacher-training-form-sktt-header-section">
            <div className="teacher-training-form-sktt-header-flex">
              <div className="teacher-training-form-sktt-logo-box">
                <img
                  src={logo}
                  alt="S.K. College Logo"
                  className="teacher-training-form-sktt-logo-img"
                />
              </div>
              <div className="teacher-training-form-sktt-text-box">
                <h1 className="teacher-training-form-sktt-hindi-title">
                  सुचेता कृपलानी शिक्षक प्रशिक्षण महाविद्यालय
                </h1>
                <p className="teacher-training-form-sktt-affiliation">
                  जय नारायण व्यास विश्वविद्यालय, जोधपुर (JNVU), राजस्थान से
                  सम्बद्ध (Affiliated) है।
                </p>
                <p className="teacher-training-form-sktt-address">
                  पता :- ग्रा. पो. – मानकलाव, जोधपुर, 342305 राज.
                </p>
              </div>
            </div>
            <div className="teacher-training-form-sktt-divider"></div>
            <div className="teacher-training-form-sktt-title-row">
              <h3>
                प्रशिक्षणार्थी आलेख – पत्र{" "}
                <span className="teacher-training-form-sktt-eng-sub">
                  (Trainee Profile – Form)
                </span>
              </h3>
            </div>
            <div className="teacher-training-form-sktt-session-box">
              <span>सत्र 20</span>
              <input
                type="text"
                name="sessionStart"
                value={formData.sessionStart}
                onChange={handleChange}
                className="teacher-training-form-sktt-session-input"
                placeholder=""
              />
              <span>- 20</span>
              <input
                type="text"
                name="sessionEnd"
                value={formData.sessionEnd}
                onChange={handleChange}
                className="teacher-training-form-sktt-session-input"
                placeholder=""
              />
            </div>
          </div>

          <form
            className="teacher-training-form-sktt-content"
            onSubmit={handleSubmit}
          >
            {/* --- 1. TRAINEE DETAILS --- */}
            <div className="teacher-training-form-sktt-section">
              <h3 className="teacher-training-form-sktt-section-title">
                <FaUser /> प्रशिक्षणार्थी विवरण (Trainee Details)
              </h3>

              <div className="teacher-training-form-sktt-profile-layout">
                {/* Photo */}
                <div className="teacher-training-form-sktt-photo-area">
                  <div
                    className="teacher-training-form-sktt-photo-box"
                    onClick={() => fileRefs.photo.current.click()}
                  >
                    {previews.photo ? (
                      <img src={previews.photo} alt="Student" />
                    ) : (
                      <div className="teacher-training-form-sktt-placeholder">
                        <FaUpload /> <span>Upload Photo</span>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileRefs.photo}
                    onChange={(e) => handleFileChange(e, "photo")}
                    accept="image/*"
                    hidden
                  />
                  <small>Passport Size (50KB-200KB)</small>
                </div>

                {/* Inputs */}
                <div className="teacher-training-form-sktt-inputs-area">
                  <div className="teacher-training-form-sktt-grid-2">
                    <div className="teacher-training-form-sktt-group">
                      <label>
                        प्रशिक्षणार्थी का नाम (अंग्रेजी में){" "}
                        <span className="teacher-training-form-sktt-req">
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        name="studentName"
                        value={formData.studentName}
                        onChange={handleChange}
                        placeholder="Name in English"
                        required
                      />
                    </div>
                    <div className="teacher-training-form-sktt-group">
                      <label>
                        जन्म तिथि / DOB{" "}
                        <span className="teacher-training-form-sktt-req">
                          *
                        </span>
                      </label>
                      <input
                        type="date"
                        name="dob"
                        value={formData.dob}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="teacher-training-form-sktt-group">
                      <label>
                        लिंग / Gender{" "}
                        <span className="teacher-training-form-sktt-req">
                          *
                        </span>
                      </label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Male">पुरुष / Male</option>
                        <option value="Female">महिला / Female</option>
                        <option value="Other">अन्य / Other</option>
                      </select>
                    </div>
                    <div className="teacher-training-form-sktt-group">
                      <label>रक्त समूह / Blood Group</label>
                      <select
                        name="bloodGroup"
                        value={formData.bloodGroup}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        <option value="A+">A+</option>
                        <option value="B+">B+</option>
                        <option value="O+">O+</option>
                        <option value="AB+">AB+</option>
                        <option value="A-">A-</option>
                        <option value="B-">B-</option>
                        <option value="O-">O-</option>
                        <option value="AB-">AB-</option>
                      </select>
                    </div>
                    <div className="teacher-training-form-sktt-group">
                      <label>
                        पिता का नाम / Father's Name{" "}
                        <span className="teacher-training-form-sktt-req">
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        name="fatherName"
                        value={formData.fatherName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="teacher-training-form-sktt-group">
                      <label>
                        माता का नाम / Mother's Name{" "}
                        <span className="teacher-training-form-sktt-req">
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        name="motherName"
                        value={formData.motherName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="teacher-training-form-sktt-grid-3 teacher-training-form-sktt-mt-3">
                    <div className="teacher-training-form-sktt-group">
                      <label>
                        आधार क्रमांक / Aadhar No.{" "}
                        <span className="teacher-training-form-sktt-req">
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        name="aadharNumber"
                        maxLength="12"
                        value={formData.aadharNumber}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    
                    {/* Religion Dropdown & Other Logic */}
                    <div className="teacher-training-form-sktt-group">
                      <label>
                        धर्म / Religion{" "}
                        <span className="teacher-training-form-sktt-req">
                          *
                        </span>
                      </label>
                      <select
                        name="religionSelect"
                        onChange={handleReligionDropdownChange}
                        value={isOtherReligion ? "Other" : formData.religion}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Hindu">Hindu</option>
                        <option value="Muslim">Muslim</option>
                        <option value="Sikh">Sikh</option>
                        <option value="Christian">Christian</option>
                        <option value="Jain">Jain</option>
                        <option value="Buddhist">Buddhist</option>
                        <option value="Parsi">Parsi</option>
                        <option value="Jewish">Jewish</option>
                        <option value="Other">Other</option>
                      </select>
                      {isOtherReligion && (
                        <input
                          type="text"
                          name="religion"
                          placeholder="Please Specify"
                          value={formData.religion}
                          onChange={handleChange}
                          style={{ marginTop: "8px" }}
                          required
                        />
                      )}
                    </div>

                    <div className="teacher-training-form-sktt-group">
                      <label>
                        जाति / Caste{" "}
                        <span className="teacher-training-form-sktt-req">
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        name="caste"
                        value={formData.caste}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  {/* Category Section */}
                  <div className="teacher-training-form-sktt-grid-2 teacher-training-form-sktt-mt-3">
                    <div className="teacher-training-form-sktt-group">
                      <label>
                        श्रेणी (Category){" "}
                        <span className="teacher-training-form-sktt-req">
                          *
                        </span>
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="General">सामान्य (General)</option>
                        <option value="OBC">अन्य पिछड़ा वर्ग (OBC)</option>
                        <option value="SC">अनुसूचित जाति (SC)</option>
                        <option value="ST">अनुसूचित जनजाति (ST)</option>
                        <option value="EWS">EWS</option>
                        <option value="MBC">MBC</option>
                      </select>
                    </div>
                    {formData.category && formData.category !== "General" && (
                      <div className="teacher-training-form-sktt-group">
                        <label>
                          प्रमाण-पत्र संख्या / Certificate No.{" "}
                          <span className="teacher-training-form-sktt-req">
                            *
                          </span>
                        </label>
                        <input
                          type="text"
                          name="categoryCertificate"
                          value={formData.categoryCertificate}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    )}
                  </div>

                  {/* Special Reservation */}
                  <div className="teacher-training-form-sktt-special-reservation teacher-training-form-sktt-mt-3">
                    <label className="teacher-training-form-sktt-sub-label">
                      अतिरिक्त आरक्षण श्रेणी / Special Reservation (If
                      Applicable)
                    </label>

                    <div className="teacher-training-form-sktt-reservation-grid">
                      {/* Card 1 */}
                      <div
                        className={`teacher-training-form-sktt-res-card ${formData.isDisabled ? "active" : ""}`}
                      >
                        <div className="teacher-training-form-sktt-res-header">
                          <input
                            type="checkbox"
                            name="isDisabled"
                            checked={formData.isDisabled}
                            onChange={handleChange}
                          />
                          <span>Disabled (विकलांग)</span>
                        </div>
                        {formData.isDisabled && (
                          <input
                            type="text"
                            name="disabledCert"
                            placeholder="Enter Cert. No."
                            value={formData.disabledCert}
                            onChange={handleChange}
                            className="teacher-training-form-sktt-res-input"
                            required
                          />
                        )}
                      </div>

                      {/* Card 2 */}
                      <div
                        className={`teacher-training-form-sktt-res-card ${formData.isExService ? "active" : ""}`}
                      >
                        <div className="teacher-training-form-sktt-res-header">
                          <input
                            type="checkbox"
                            name="isExService"
                            checked={formData.isExService}
                            onChange={handleChange}
                          />
                          <span>Ex-Serviceman (सैनिक)</span>
                        </div>
                        {formData.isExService && (
                          <input
                            type="text"
                            name="exServiceCert"
                            placeholder="Enter Cert. No."
                            value={formData.exServiceCert}
                            onChange={handleChange}
                            className="teacher-training-form-sktt-res-input"
                            required
                          />
                        )}
                      </div>

                      {/* Card 3 */}
                      <div
                        className={`teacher-training-form-sktt-res-card ${formData.isWidow ? "active" : ""}`}
                      >
                        <div className="teacher-training-form-sktt-res-header">
                          <input
                            type="checkbox"
                            name="isWidow"
                            checked={formData.isWidow}
                            onChange={handleChange}
                          />
                          <span>Widow (विधवा)</span>
                        </div>
                        {formData.isWidow && (
                          <input
                            type="text"
                            name="widowCert"
                            placeholder="Enter Cert. No."
                            value={formData.widowCert}
                            onChange={handleChange}
                            className="teacher-training-form-sktt-res-input"
                            required
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* --- 2. CONTACT DETAILS --- */}
            <div className="teacher-training-form-sktt-section">
              <h3 className="teacher-training-form-sktt-section-title">
                <FaMapMarkerAlt /> संपर्क जानकारी (Contact Details)
              </h3>
              <div className="teacher-training-form-sktt-grid-1">
                <div className="teacher-training-form-sktt-group">
                  <label>
                    1. स्थायी पता / Permanent Address (With Pincode){" "}
                    <span className="teacher-training-form-sktt-req">*</span>
                  </label>
                  <textarea
                    name="permanentAddress"
                    value={formData.permanentAddress}
                    onChange={handleChange}
                    onBlur={handleAddressBlur}
                    rows="2"
                    placeholder="Village, Post, Dist, State, Pincode"
                    required
                  ></textarea>
                </div>

                <div className="teacher-training-form-sktt-checkbox-block">
                  <input
                    type="checkbox"
                    name="sameAddress"
                    id="sameAdd"
                    checked={formData.sameAddress}
                    onChange={handleChange}
                  />
                  <label htmlFor="sameAdd">
                    अगर समान है तो (Same as Permanent Address)
                  </label>
                </div>

                <div className="teacher-training-form-sktt-group">
                  <label>
                    2. वर्तमान पता / Current Address (With Pincode){" "}
                    <span className="teacher-training-form-sktt-req">*</span>
                  </label>
                  <textarea
                    name="currentAddress"
                    value={formData.currentAddress}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Village, Post, Dist, State, Pincode"
                    required
                    readOnly={formData.sameAddress}
                  ></textarea>
                </div>
              </div>
              <div className="teacher-training-form-sktt-grid-2 teacher-training-form-sktt-mt-3">
                <div className="teacher-training-form-sktt-group">
                  <label>
                    3. मोबाइल नंबर / Mobile Number{" "}
                    <span className="teacher-training-form-sktt-req">*</span>
                  </label>
                  <div className="teacher-training-form-sktt-prefix-wrap">
                    <span>+91</span>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      maxLength="10"
                      placeholder="XXXXX-XXXXX"
                      required
                    />
                  </div>
                </div>
                <div className="teacher-training-form-sktt-group">
                  <label>
                    4. ईमेल आईडी / Email ID{" "}
                    <span className="teacher-training-form-sktt-req">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* --- 3. SERVICE DETAILS --- */}
            <div className="teacher-training-form-sktt-section">
              <h3 className="teacher-training-form-sktt-section-title">
                <FaBriefcase /> सेवा विवरण (Service Details)
              </h3>
              <p className="teacher-training-form-sktt-note">
                यदि आप सेवायत है तो उसका विवरण दें (अनापत्ति – पत्र एवं मुक्ति
                प्रमाण–पत्र संलग्न करें) / If in service, attach NOC.
              </p>
              <div className="teacher-training-form-sktt-grid-3">
                <div className="teacher-training-form-sktt-group">
                  <label>पद / Designation</label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                  />
                </div>
                <div className="teacher-training-form-sktt-group">
                  <label>विभाग / Department</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                  />
                </div>
                <div className="teacher-training-form-sktt-group">
                  <label>पदस्थापना पता / Posting Address</label>
                  <input
                    type="text"
                    name="postingAddress"
                    value={formData.postingAddress}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* --- 4. COUNSELING DETAILS --- */}
            <div className="teacher-training-form-sktt-section">
              <h3 className="teacher-training-form-sktt-section-title">
                <FaListOl /> काउंसलिंग विवरण (Counseling Details)
              </h3>
              <div className="teacher-training-form-sktt-grid-3">
                <div className="teacher-training-form-sktt-group">
                  <label>संकाय / Faculty</label>
                  <input
                    type="text"
                    name="faculty"
                    value={formData.faculty}
                    onChange={handleChange}
                  />
                </div>
                <div className="teacher-training-form-sktt-group">
                  <label>प्रवेश क्रम संख्या / Admission Serial No.</label>
                  <input
                    type="text"
                    name="admissionSerial"
                    value={formData.admissionSerial}
                    onChange={handleChange}
                  />
                </div>
                <div className="teacher-training-form-sktt-group">
                  <label>श्रेणी / Category</label>
                  <input
                    type="text"
                    name="counselingCategory"
                    value={formData.counselingCategory}
                    onChange={handleChange}
                  />
                </div>
                <div className="teacher-training-form-sktt-group">
                  <label>अनुक्रमांक (प्रवेश परीक्षा) / Roll No.</label>
                  <input
                    type="text"
                    name="rollNo"
                    value={formData.rollNo}
                    onChange={handleChange}
                  />
                </div>
                <div className="teacher-training-form-sktt-group">
                  <label>कुल प्राप्तांक / Total Marks</label>
                  <input
                    type="text"
                    name="totalEntranceMarks"
                    value={formData.totalEntranceMarks}
                    onChange={handleChange}
                  />
                </div>
                <div className="teacher-training-form-sktt-group">
                  <label>वरीयता क्रम / Merit Rank</label>
                  <input
                    type="text"
                    name="meritRank"
                    value={formData.meritRank}
                    onChange={handleChange}
                  />
                </div>
                <div className="teacher-training-form-sktt-group">
                  <label>जिला / District</label>
                  <input
                    type="text"
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* --- 5. EDUCATIONAL QUALIFICATIONS (TABLE) --- */}
            <div className="teacher-training-form-sktt-section">
              <h3 className="teacher-training-form-sktt-section-title">
                <FaGraduationCap /> शैक्षणिक योग्यता (Educational
                Qualifications)
              </h3>

              <div className="teacher-training-form-sktt-table-wrapper">
                <table className="teacher-training-form-sktt-edu-table">
                  <thead>
                    <tr>
                      <th>
                        नाम परीक्षा <br />
                        (Exam Name)
                      </th>
                      <th>
                        कुल अंक <br />
                        (Total)
                      </th>
                      <th>
                        प्राप्तांक <br />
                        (Obt.)
                      </th>
                      <th>
                        प्रतिशत <br />
                        (%)
                      </th>
                      <th>
                        श्रेणी <br />
                        (Div)
                      </th>
                      <th>
                        विषय <br />
                        (Subjects)
                      </th>
                      <th>
                        बोर्ड / वि.वि. <br />
                        (Board/Univ)
                      </th>
                      <th>
                        संलग्न संख्या <br />
                        (Cert. No)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="teacher-training-form-sktt-fixed-col">
                        हाई स्कूल / माध्यमिक
                        <br />
                        (Secondary)
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHighSchool.total}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHighSchool",
                              "total",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHighSchool.obtained}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHighSchool",
                              "obtained",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHighSchool.percent}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHighSchool",
                              "percent",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHighSchool.div}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHighSchool",
                              "div",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHighSchool.subj}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHighSchool",
                              "subj",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHighSchool.board}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHighSchool",
                              "board",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHighSchool.cert}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHighSchool",
                              "cert",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="teacher-training-form-sktt-fixed-col">
                        उच्च माध्यमिक
                        <br />
                        (Higher Sec.)
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHigherSec.total}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHigherSec",
                              "total",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHigherSec.obtained}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHigherSec",
                              "obtained",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHigherSec.percent}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHigherSec",
                              "percent",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHigherSec.div}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHigherSec",
                              "div",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHigherSec.subj}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHigherSec",
                              "subj",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHigherSec.board}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHigherSec",
                              "board",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduHigherSec.cert}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduHigherSec",
                              "cert",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="teacher-training-form-sktt-fixed-col">
                        स्नातक (Graduation){" "}
                        <span className="teacher-training-form-sktt-req">
                          *
                        </span>
                      </td>
                      <td>
                        <input
                          type="text"
                          required
                          value={formData.eduGraduation.total}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduGraduation",
                              "total",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          required
                          value={formData.eduGraduation.obtained}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduGraduation",
                              "obtained",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          required
                          value={formData.eduGraduation.percent}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduGraduation",
                              "percent",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          required
                          value={formData.eduGraduation.div}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduGraduation",
                              "div",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          required
                          value={formData.eduGraduation.subj}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduGraduation",
                              "subj",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          required
                          value={formData.eduGraduation.board}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduGraduation",
                              "board",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          required
                          value={formData.eduGraduation.cert}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduGraduation",
                              "cert",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="teacher-training-form-sktt-fixed-col">
                        स्नातकोत्तर
                        <br />
                        (Post Grad.)
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduPostGrad.total}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduPostGrad",
                              "total",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduPostGrad.obtained}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduPostGrad",
                              "obtained",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduPostGrad.percent}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduPostGrad",
                              "percent",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduPostGrad.div}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduPostGrad",
                              "div",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduPostGrad.subj}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduPostGrad",
                              "subj",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduPostGrad.board}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduPostGrad",
                              "board",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduPostGrad.cert}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduPostGrad",
                              "cert",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="teacher-training-form-sktt-fixed-col">
                        अन्य (Others)
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduOther.total}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduOther",
                              "total",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduOther.obtained}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduOther",
                              "obtained",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduOther.percent}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduOther",
                              "percent",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduOther.div}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduOther",
                              "div",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduOther.subj}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduOther",
                              "subj",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduOther.board}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduOther",
                              "board",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.eduOther.cert}
                          onChange={(e) =>
                            handleEducationChange(
                              "eduOther",
                              "cert",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* --- 6. TRAINING SUBJECTS --- */}
            <div className="teacher-training-form-sktt-section">
              <h3 className="teacher-training-form-sktt-section-title">
                <FaBookReader /> प्रशिक्षण हेतु चयनित विषय (Subjects Selected
                for Training)
              </h3>
              <p className="teacher-training-form-sktt-note">
                (केवल B.Ed. हेतु / For B.Ed Only)
              </p>
              <div className="teacher-training-form-sktt-grid-1">
                <div className="teacher-training-form-sktt-group">
                  <label>1. Teaching Subject 1</label>
                  <input
                    type="text"
                    name="trainingSub1"
                    value={formData.trainingSub1}
                    onChange={handleChange}
                    placeholder="___________________"
                  />
                </div>
                <div className="teacher-training-form-sktt-group">
                  <label>2. Teaching Subject 2</label>
                  <input
                    type="text"
                    name="trainingSub2"
                    value={formData.trainingSub2}
                    onChange={handleChange}
                    placeholder="___________________"
                  />
                </div>
              </div>
            </div>

            {/* --- FOOTER --- */}
            <div className="teacher-training-form-sktt-final-section">
              {/* Center Aligned Captcha (Code Left, Input Right) */}
              <div className="teacher-training-form-sktt-captcha-wrapper">
                <label className="teacher-training-form-sktt-captcha-label">
                  Security Check:
                </label>

                <div className="teacher-training-form-sktt-captcha-display">
                  <span className="teacher-training-form-sktt-captcha-text">
                    {captcha}
                  </span>
                  <button
                    type="button"
                    className="teacher-training-form-sktt-refresh-btn"
                    onClick={generateCaptcha}
                  >
                    <FaSyncAlt />
                  </button>
                </div>

                <input
                  type="text"
                  className="teacher-training-form-sktt-captcha-input-field"
                  placeholder="Enter Code"
                  value={userCaptcha}
                  onChange={(e) => setUserCaptcha(e.target.value)}
                  required
                />
              </div>

              <div className="teacher-training-form-sktt-declaration">
                <label>
                  <input
                    type="checkbox"
                    name="declaration"
                    checked={formData.declaration}
                    onChange={handleChange}
                  />
                  <span>
                    मेरे द्वारा दी गई जानकारी सत्य है। यदि कोई जानकारी गलत पाई
                    जाती है तो प्रवेश रद्द किया जा सकता है।
                    <br />
                    <small>
                      (I hereby declare that the information provided above is
                      true. If found false, admission may be cancelled.)
                    </small>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                className="teacher-training-form-sktt-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? "Submitting..." : "Submit Application"}{" "}
                <FaPaperPlane />
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AdmissionForm;